import { create } from "zustand";

const sortByTime = (a, b) => {
  const ta = new Date(a.createdAt || a.created_at || 0).getTime();
  const tb = new Date(b.createdAt || b.created_at || 0).getTime();
  return ta - tb;
};

const IMAGE_EXTS = new Set([
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "bmp",
  "heic",
  "heif",
  "svg",
]);
const VIDEO_EXTS = new Set([
  "mp4",
  "mov",
  "mkv",
  "avi",
  "m4v",
  "3gp",
]);
const AUDIO_EXTS = new Set([
  "mp3",
  "m4a",
  "aac",
  "wav",
  "ogg",
  "webm",
  "opus",
  "amr",
  "caf",
]);
const FILE_EXTS = new Set([
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
  "txt",
  "zip",
  "csv",
]);

const AUDIO_TYPE_ALIASES = new Set([
  "audio",
  "record",
  "voice",
  "voice_note",
  "voicenote",
  "recording",
]);
const IMAGE_TYPE_ALIASES = new Set(["image", "photo", "picture"]);
const VIDEO_TYPE_ALIASES = new Set(["video", "clip"]);
const FILE_TYPE_ALIASES = new Set([
  "files",
  "file",
  "document",
  "doc",
  "attachment",
]);

/** Strip JSON that sometimes gets glued onto media URLs (…jpg%22,%22timestamp…). */
const sanitizeMediaUrl = (raw) => {
  if (raw == null) return null;
  let url = String(raw).trim();
  if (!url) return null;

  const leak = url.match(
    /(\.(?:jpe?g|png|gif|webp|bmp|heic|heif|svg|pdf|mp4|mov|mkv|avi|m4v|3gp|m4a|mp3|wav|ogg|webm|aac|opus|amr|caf|docx?|xlsx?|pptx?|zip|txt|csv))(?:%22|",)/i,
  );
  if (leak) {
    url = url.slice(0, leak.index + leak[1].length);
  }
  return url;
};

const extFromUrl = (url) => {
  if (!url) return "";
  try {
    const path = decodeURIComponent(new URL(url, "https://local").pathname);
    const match = path.match(/\.([a-z0-9]+)$/i);
    return match ? match[1].toLowerCase() : "";
  } catch {
    const match = String(url).match(/\.([a-z0-9]+)(?:\?|#|$)/i);
    return match ? match[1].toLowerCase() : "";
  }
};

const fileNameFromUrl = (url) => {
  if (!url) return null;
  try {
    const path = decodeURIComponent(new URL(url, "https://local").pathname);
    const base = path.split("/").filter(Boolean).pop();
    return base || null;
  } catch {
    const match = String(url).match(/\/([^/?#]+)(?:\?|#|$)/);
    return match ? decodeURIComponent(match[1]) : null;
  }
};

/**
 * Map server types to UI types aligned with backend ChatMessageType:
 * text | image | video | files | audio | record | system
 */
const resolveMessageType = (rawType, mediaUrl) => {
  const type = String(rawType || "").toLowerCase().trim();
  if (type === "system") return "system";

  const ext = extFromUrl(mediaUrl);

  // Extension wins when the server mislabels (e.g. PDF as "image")
  if (ext && FILE_EXTS.has(ext)) return "files";

  // iOS MediaRecorder stores AAC voice notes in an .mp4 container.
  // Keep explicit audio/record types as audio — do not treat them as video.
  if (
    AUDIO_TYPE_ALIASES.has(type) &&
    (!ext || AUDIO_EXTS.has(ext) || ext === "mp4")
  ) {
    return type === "record" ? "record" : "audio";
  }

  if (ext && VIDEO_EXTS.has(ext)) return "video";
  if (ext && AUDIO_EXTS.has(ext)) {
    return type === "record" ? "record" : "audio";
  }
  if (ext && IMAGE_EXTS.has(ext)) return "image";

  if (AUDIO_TYPE_ALIASES.has(type)) {
    return type === "record" ? "record" : "audio";
  }
  if (IMAGE_TYPE_ALIASES.has(type)) return "image";
  if (VIDEO_TYPE_ALIASES.has(type)) return "video";
  if (FILE_TYPE_ALIASES.has(type)) return "files";
  if (type === "text" || !type) return mediaUrl ? "files" : "text";
  if (mediaUrl) return "files";
  return type || "text";
};

const normalizeMessage = (raw, { guestId, hostId } = {}) => {
  if (!raw || typeof raw !== "object") return null;

  const text = raw.text ?? raw.message ?? raw.content ?? "";
  const rawType = String(raw.messageType || raw.message_type || "").toLowerCase();

  // Live verification status events often arrive without an id. Collapse them
  // so a revoke replaces the previous synthetic status instead of stacking.
  const stableVerificationId =
    rawType === "system" && /identity\s+verified/i.test(String(text))
      ? "__identity_verified__"
      : rawType === "system" && /verification\s+revoked/i.test(String(text))
        ? "__verification_revoked__"
        : null;

  const id =
    raw._id ??
    raw.id ??
    raw.messageId ??
    raw.message_id ??
    raw.uuid ??
    raw.privateMessageId ??
    (raw.tempId ? `temp:${raw.tempId}` : null) ??
    stableVerificationId;

  const mediaUrlRaw =
    raw.audioUrl ??
    raw.audio_url ??
    raw.imageUrl ??
    raw.image_url ??
    raw.videoUrl ??
    raw.video_url ??
    raw.fileUrl ??
    raw.file_url ??
    raw.mediaUrl ??
    raw.media_url ??
    raw.url ??
    null;

  const mediaUrl = sanitizeMediaUrl(mediaUrlRaw);

  const fileName =
    raw.fileName ||
    raw.file_name ||
    raw.filename ||
    raw.name ||
    fileNameFromUrl(mediaUrl) ||
    null;

  const resolvedId =
    id != null
      ? String(id)
      : `srv:${raw.timestamp || raw.createdAt || Date.now()}:${text.slice(0, 40)}`;

  const senderId =
    raw.sender?.id ??
    raw.senderId ??
    raw.sender_id ??
    raw.fromId ??
    raw.from ??
    raw.userId ??
    raw.user_id ??
    null;

  const isGuest =
    raw.isGuest === true ||
    raw.fromSelf === true ||
    raw.role === "guest" ||
    raw.senderType === "guest" ||
    raw.sender_type === "guest" ||
    raw.sender?.name === "Guest" ||
    (guestId != null && senderId != null && String(senderId) === String(guestId));

  const isHost =
    !isGuest &&
    (raw.role === "host" ||
      raw.senderType === "host" ||
      raw.sender_type === "host" ||
      (hostId != null && senderId != null && String(senderId) === String(hostId)) ||
      raw.isHost === true ||
      (senderId != null && guestId != null && String(senderId) !== String(guestId)));

  const seen = raw.isSeen === true || raw.status === "seen";
  const messageType = resolveMessageType(
    raw.messageType || raw.message_type,
    mediaUrl,
  );

  const replyRaw = raw.replyTo || raw.reply_to || null;
  const replyTo =
    replyRaw && typeof replyRaw === "object"
      ? {
          messageId:
            replyRaw.messageId != null
              ? String(replyRaw.messageId)
              : replyRaw.message_id != null
                ? String(replyRaw.message_id)
                : replyRaw._id != null
                  ? String(replyRaw._id)
                  : replyRaw.id != null
                    ? String(replyRaw.id)
                    : null,
          messageType: String(
            replyRaw.messageType || replyRaw.message_type || "text",
          ).toLowerCase(),
          textSnippet: String(
            replyRaw.textSnippet ||
              replyRaw.text_snippet ||
              replyRaw.text ||
              "",
          ),
          senderId:
            replyRaw.sender?.id != null
              ? String(replyRaw.sender.id)
              : replyRaw.senderId != null
                ? String(replyRaw.senderId)
                : replyRaw.sender_id != null
                  ? String(replyRaw.sender_id)
                  : null,
          senderName:
            replyRaw.sender?.name ||
            replyRaw.senderName ||
            replyRaw.sender_name ||
            null,
        }
      : null;

  return {
    id: resolvedId,
    tempId: raw.tempId || null,
    text,
    messageType,
    note: raw.note != null && raw.note !== "" ? String(raw.note) : null,
    audioUrl: mediaUrl,
    mediaUrl,
    fileName: fileName ? String(fileName) : null,
    localPreview: raw.localPreview || null,
    replyTo,
    createdAt:
      raw.timestamp ||
      raw.createdAt ||
      raw.created_at ||
      new Date().toISOString(),
    senderId: senderId != null ? String(senderId) : null,
    senderName: raw.sender?.name || raw.senderName || null,
    receiverId:
      raw.receiverId != null
        ? String(raw.receiverId)
        : raw.receiver_id != null
          ? String(raw.receiver_id)
          : null,
    status: raw.status === "pending" ? "pending" : seen ? "seen" : "sent",
    isSeen: seen,
    isMine: Boolean(isGuest),
    isHost: Boolean(isHost) && !isGuest,
    raw,
  };
};

/**
 * Parse a server message's `emojis` field into the reactions map shape:
 * { EMOJI_KEY: { count, mine } }. Returns null when there are no reactions.
 * Shape from backend: { summary: { THUMBS_UP: { count } }, details: [{ userId, emoji }] }
 */
const extractMessageReactions = (raw, guestId) => {
  const emojis = raw?.emojis;
  if (!emojis || typeof emojis !== "object") return null;

  const summary = emojis.summary || {};
  const details = Array.isArray(emojis.details) ? emojis.details : [];
  const result = {};

  Object.entries(summary).forEach(([emojiKey, val]) => {
    const count = Number(val?.count) || 0;
    if (count <= 0) return;
    const key = String(emojiKey).toUpperCase();
    const mine =
      guestId != null &&
      details.some(
        (d) =>
          d?.userId != null &&
          String(d.userId) === String(guestId) &&
          String(d?.emoji || "").toUpperCase() === key,
      );
    result[key] = { count, mine };
  });

  return Object.keys(result).length > 0 ? result : null;
};

const rawMessageId = (raw) =>
  raw?._id ?? raw?.id ?? raw?.messageId ?? raw?.message_id ?? null;

/**
 * A user may have only one reaction per message. Decrement/drop any
 * previous `mine` emoji so changing a reaction replaces it instead of stacking.
 */
const withoutMineReaction = (msgReactions, exceptKey) => {
  const next = { ...msgReactions };
  Object.keys(next).forEach((k) => {
    if (exceptKey && k === exceptKey) return;
    if (!next[k]?.mine) return;
    const newCount = Math.max(0, next[k].count - 1);
    if (newCount === 0) {
      delete next[k];
    } else {
      next[k] = { count: newCount, mine: false };
    }
  });
  return next;
};

const mergeMessages = (existing, incomingList, ctx) => {
  const byId = new Map(existing.map((m) => [m.id, m]));

  incomingList.forEach((raw) => {
    const msg = normalizeMessage(raw, ctx);
    if (!msg) return;

    // Prefer exact tempId, else newest pending guest bubble with same text
    let pending = null;
    if (msg.tempId) {
      pending = existing.find((m) => m.tempId === msg.tempId);
    }
    if (!pending && msg.text) {
      const candidates = existing.filter(
        (m) =>
          (m.status === "pending" || m.status === "failed") &&
          m.isMine &&
          m.tempId &&
          m.text === msg.text,
      );
      pending = candidates[candidates.length - 1] || null;
    }
    // If server echo is ours but text match failed, still clear oldest pending
    if (!pending && msg.isMine) {
      const candidates = existing.filter(
        (m) => m.status === "pending" && m.isMine && m.tempId,
      );
      pending = candidates[candidates.length - 1] || null;
    }

    if (pending) {
      byId.delete(pending.id);
    }

    const prev = byId.get(msg.id);
    const merged = {
      ...prev,
      ...msg,
      tempId: null,
    };
    const seen = Boolean(
      merged.isSeen ||
        merged.status === "seen" ||
        prev?.isSeen ||
        prev?.status === "seen",
    );
    byId.set(msg.id, {
      ...merged,
      isSeen: seen,
      status: seen ? "seen" : "sent",
    });
  });

  return Array.from(byId.values()).sort(sortByTime);
};

const useChatStore = create((set, get) => ({
  connectionStatus: "idle", // idle | connecting | connected | reconnecting | error
  connectionError: null,
  guestId: null,
  hostId: null,
  qrCode: null,
  unitName: null,
  hostOnline: false,
  hostName: null,
  hostAvatar: null,
  hostTyping: false,
  messages: [],
  reactions: {}, // { [messageId]: { [EMOJI_KEY]: { count: number, mine: boolean } } }
  historyLoading: false,
  historyLoaded: false,
  historyError: null,
  skipHistoryReplace: false,
  sendInFlight: false,
  lastReclaimAt: 0,
  reclaiming: false,
  reclaimError: null,

  reset: () =>
    set({
      connectionStatus: "idle",
      connectionError: null,
      guestId: null,
      hostId: null,
      qrCode: null,
      unitName: null,
      hostOnline: false,
      hostName: null,
      hostAvatar: null,
      hostTyping: false,
      messages: [],
      reactions: {},
      historyLoading: false,
      historyLoaded: false,
      historyError: null,
      skipHistoryReplace: false,
      sendInFlight: false,
      lastReclaimAt: 0,
      reclaiming: false,
      reclaimError: null,
    }),

  setContext: ({ hostId, qrCode, unitName, guestId }) =>
    set({
      hostId: hostId != null ? String(hostId) : null,
      qrCode: qrCode || null,
      unitName: unitName || null,
      guestId: guestId != null ? String(guestId) : get().guestId,
    }),

  setConnectionStatus: (connectionStatus, connectionError = null) =>
    set({ connectionStatus, connectionError }),

  setGuestId: (guestId) =>
    set({ guestId: guestId != null ? String(guestId) : null }),

  setHostOnline: (hostOnline) => set({ hostOnline: Boolean(hostOnline) }),

  setHostDetails: (hostDetails) => {
    if (!hostDetails || typeof hostDetails !== "object") return;
    set({
      hostName: hostDetails.name || get().hostName,
      hostAvatar: hostDetails.profilePictureUrl || hostDetails.profile_picture_url || get().hostAvatar,
      hostOnline:
        typeof hostDetails.isOnline === "boolean"
          ? hostDetails.isOnline
          : get().hostOnline,
    });
  },

  setHostTyping: (hostTyping) => set({ hostTyping: Boolean(hostTyping) }),

  // Loading and error must change in a single set(): under the app's legacy
  // ReactDOM.render root each set() from a socket callback renders on its own,
  // and a "not loading, no error" render makes useHostChat request history again.
  startHistoryLoad: () => set({ historyLoading: true, historyError: null }),

  failHistoryLoad: (historyError) => set({ historyLoading: false, historyError }),

  setMessagesFromHistory: (list) => {
    const { guestId, hostId, skipHistoryReplace } = get();
    if (skipHistoryReplace) {
      set({ historyLoading: false, historyLoaded: true });
      return;
    }

    const arr = Array.isArray(list)
      ? list
      : list?.messages || list?.data || list?.items || [];

    // Hydrate reactions from each message's `emojis` field
    const reactions = {};
    arr.forEach((raw) => {
      const id = rawMessageId(raw);
      if (id == null) return;
      const parsed = extractMessageReactions(raw, guestId);
      if (parsed) reactions[String(id)] = parsed;
    });

    set({
      messages: mergeMessages([], arr, { guestId, hostId }),
      reactions,
      historyLoading: false,
      historyLoaded: true,
      historyError: null,
    });
  },

  upsertMessage: (raw) => {
    const { guestId, hostId, messages, reactions } = get();

    // Sync reactions when the live message carries an `emojis` payload
    const id = rawMessageId(raw);
    const parsed = id != null ? extractMessageReactions(raw, guestId) : null;
    const nextReactions = parsed
      ? { ...reactions, [String(id)]: parsed }
      : reactions;

    set({
      messages: mergeMessages(messages, [raw], { guestId, hostId }),
      reactions: nextReactions,
      hostTyping: false,
    });
  },

  addOptimisticMessage: ({
    text,
    tempId,
    messageType = "text",
    mediaUrl = null,
    fileName = null,
    localPreview = null,
  }) => {
    const { guestId, hostId, messages } = get();
    const optimistic = normalizeMessage(
      {
        id: `temp:${tempId}`,
        tempId,
        text: text || "",
        messageType,
        audioUrl: mediaUrl,
        fileName,
        localPreview,
        senderId: guestId,
        receiverId: hostId,
        createdAt: new Date().toISOString(),
        status: "pending",
        isGuest: true,
      },
      { guestId, hostId },
    );
    set({ messages: [...messages, optimistic].sort(sortByTime) });
    return optimistic;
  },

  markMessageFailed: (tempId) => {
    set({
      messages: get().messages.map((m) =>
        m.tempId === tempId || m.id === `temp:${tempId}`
          ? { ...m, status: "failed" }
          : m,
      ),
      sendInFlight: false,
    });
  },

  markMessageSent: (tempId, serverMsg, ackMeta = null) => {
    const { guestId, hostId, messages } = get();
    const stillThere = messages.some(
      (m) => m.tempId === tempId || m.id === `temp:${tempId}`,
    );
    if (!stillThere) {
      set({ sendInFlight: false });
      return;
    }

    const withoutTemp = messages.filter(
      (m) => m.tempId !== tempId && m.id !== `temp:${tempId}`,
    );
    const seen =
      ackMeta?.isSeen === true ||
      serverMsg?.isSeen === true ||
      serverMsg?.status === "seen";
    const realId =
      (ackMeta?.messageId != null ? String(ackMeta.messageId) : null) ||
      (serverMsg?._id != null ? String(serverMsg._id) : null) ||
      (serverMsg?.id != null ? String(serverMsg.id) : null) ||
      (serverMsg?.messageId != null ? String(serverMsg.messageId) : null);

    if (serverMsg) {
      set({
        messages: mergeMessages(
          withoutTemp,
          [
            {
              ...serverMsg,
              ...(realId ? { _id: realId, id: realId } : {}),
              ...(seen ? { isSeen: true } : {}),
              tempId,
              isGuest: true,
              senderId: serverMsg.senderId || serverMsg.sender?.id || guestId,
            },
          ],
          { guestId, hostId },
        ),
        sendInFlight: false,
      });
      return;
    }

    set({
      messages: messages.map((m) => {
        if (m.tempId !== tempId && m.id !== `temp:${tempId}`) return m;
        return {
          ...m,
          id: realId || m.id,
          tempId: null,
          status: seen ? "seen" : "sent",
          isSeen: seen,
        };
      }),
      sendInFlight: false,
    });
  },

  /**
   * Apply a late send ack after the optimistic bubble was already settled
   * (e.g. fallback timeout fired before the socket ack arrived).
   */
  applySendAck: (tempId, { messageId, isSeen } = {}) => {
    if (!tempId && !messageId) return;
    let changed = false;
    const next = get().messages.map((m) => {
      const match =
        (tempId && (m.tempId === tempId || m.id === `temp:${tempId}`)) ||
        (messageId && String(m.id) === String(messageId));
      if (!match) return m;
      changed = true;
      return {
        ...m,
        ...(messageId ? { id: String(messageId) } : {}),
        tempId: null,
        status:
          isSeen || m.isSeen || m.status === "seen"
            ? "seen"
            : m.status === "pending"
              ? "sent"
              : m.status,
        isSeen: Boolean(isSeen || m.isSeen),
      };
    });
    if (changed) set({ messages: next, sendInFlight: false });
  },

  /**
   * Live read receipts (privateMessageSeen).
   * Backend usually sends { lastMessageId } — mark all of my sent messages
   * up to that id as seen (WhatsApp-style). Also accepts messageIds[].
   */
  markMessagesSeen: ({ lastMessageId, messageIds } = {}) => {
    const { messages } = get();
    if (!messages.length) return;

    let shouldMark = null;

    if (Array.isArray(messageIds) && messageIds.length > 0) {
      const idSet = new Set(messageIds.map(String));
      shouldMark = (m) => idSet.has(String(m.id));
    } else if (lastMessageId != null && String(lastMessageId)) {
      const lastId = String(lastMessageId);
      const lastIdx = messages.findIndex((m) => String(m.id) === lastId);
      if (lastIdx >= 0) {
        shouldMark = (_m, idx) => idx <= lastIdx;
      } else {
        // Id not in local list yet — mark every already-sent guest message
        shouldMark = () => true;
      }
    } else {
      return;
    }

    let changed = false;
    const next = messages.map((m, idx) => {
      if (!m.isMine) return m;
      if (m.isSeen || m.status === "seen") return m;
      if (m.status === "pending" || m.status === "failed") return m;
      if (!shouldMark(m, idx)) return m;
      changed = true;
      return { ...m, isSeen: true, status: "seen" };
    });

    if (changed) set({ messages: next });
  },

  setSendInFlight: (sendInFlight) => set({ sendInFlight }),

  markReclaimed: () => set({ lastReclaimAt: Date.now() }),

  setReclaiming: (reclaiming) => set({ reclaiming: Boolean(reclaiming) }),

  setReclaimError: (reclaimError) => set({ reclaimError: reclaimError || null }),

  removeTempMessage: (tempId) => {
    set({
      messages: get().messages.filter(
        (m) => m.tempId !== tempId && m.id !== `temp:${tempId}`,
      ),
    });
  },

  clearAllMessages: () => {
    set({
      messages: [],
      reactions: {},
      sendInFlight: false,
      hostTyping: false,
      historyLoading: false,
      skipHistoryReplace: true,
    });
  },

  applyAllMessagesRemoved: (payload) => {
    const data = payload && typeof payload === "object" ? payload : null;
    const eventQr =
      data?.qrCode || data?.unitDetails?.qrCode || data?.unit_details?.qrCode || null;
    const { qrCode } = get();

    // Only skip if the payload clearly belongs to a different unit.
    if (eventQr && qrCode && String(eventQr) !== String(qrCode)) return;

    get().clearAllMessages();
  },

  applyEmojiAdded: ({ messageId, emoji, senderId, myId }) => {
    if (!messageId || !emoji) return;
    const key = String(emoji).toUpperCase();
    const mine = myId != null && senderId != null && String(senderId) === String(myId);
    const prev = get().reactions;
    const msgPrev = prev[messageId] || {};
    const existing = msgPrev[key] || { count: 0, mine: false };

    // Already applied optimistically for this guest — don't double-count echo
    if (mine && existing.mine) return;

    const base = mine ? withoutMineReaction(msgPrev, key) : msgPrev;
    const baseExisting = base[key] || { count: 0, mine: false };

    set({
      reactions: {
        ...prev,
        [messageId]: {
          ...base,
          [key]: {
            count: baseExisting.count + 1,
            mine: baseExisting.mine || mine,
          },
        },
      },
    });
  },

  applyEmojiRemoved: ({ messageId, emoji, senderId, myId }) => {
    if (!messageId || !emoji) return;
    const key = String(emoji).toUpperCase();
    const mine = myId != null && senderId != null && String(senderId) === String(myId);
    const prev = get().reactions;
    const msgPrev = prev[messageId] || {};
    const existing = msgPrev[key];
    if (!existing) return;
    const newCount = Math.max(0, existing.count - 1);
    const nextMsgReactions = { ...msgPrev };
    if (newCount === 0) {
      delete nextMsgReactions[key];
    } else {
      nextMsgReactions[key] = {
        count: newCount,
        mine: mine ? false : existing.mine,
      };
    }
    const nextReactions = { ...prev };
    if (Object.keys(nextMsgReactions).length === 0) {
      delete nextReactions[messageId];
    } else {
      nextReactions[messageId] = nextMsgReactions;
    }
    set({ reactions: nextReactions });
  },
}));

export default useChatStore;
